/**
 * Connect a SynLedger company: the agency makes a read-only "analytics" API
 * client in SynLedger (Settings → Integrations), pastes its client ID and
 * secret here, and SynAnalytics stores them encrypted (write-only: they are
 * never shown again) and starts the first sync. With `existing`, it replaces
 * the key of an account already connected ("Update key").
 */

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { integrationsApi } from './integrations-api.ts';

export function ConnectSynledger({
  organisationId,
  existing,
  onConnected,
  onClose,
}: {
  organisationId: string;
  existing?: { id: string; name: string } | undefined;
  onConnected: () => void;
  onClose: () => void;
}): ReactNode {
  const [name, setName] = useState(existing?.name ?? '');
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const first = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    first.current?.focus();
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      const connectionId =
        existing?.id ?? (await integrationsApi.create(organisationId, 'synledger', name.trim())).id;
      await integrationsApi.storeCredential(organisationId, connectionId, {
        client_id: clientId.trim(),
        client_secret: secret.trim(),
      });
      await integrationsApi.sync(organisationId, connectionId);
      onConnected();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'SynLedger could not be connected.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rc-scrim"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <form
        className="rc"
        role="dialog"
        aria-modal="true"
        aria-labelledby="connect-synledger-title"
        onSubmit={(event) => void submit(event)}
      >
        <h2 id="connect-synledger-title">
          {existing ? `Update the key for ${existing.name}` : 'Connect SynLedger'}
        </h2>
        <ol className="ig-steps">
          <li>
            In SynLedger, open <strong>Settings → Integrations</strong>.
          </li>
          <li>
            Create an API client with the <strong>analytics</strong> scope (read-only: it can only
            read the reporting export).
          </li>
          <li>Paste its client ID and secret below.</li>
        </ol>
        {!existing && (
          <label className="rs-field">
            Name (e.g. the company in SynLedger)
            <input
              ref={first}
              value={name}
              maxLength={120}
              required
              onChange={(event) => setName(event.target.value)}
            />
          </label>
        )}
        <label className="rs-field">
          Client ID
          <input
            ref={existing ? first : undefined}
            value={clientId}
            autoComplete="off"
            required
            onChange={(event) => setClientId(event.target.value)}
          />
        </label>
        <label className="rs-field">
          Client secret
          <input
            type="password"
            value={secret}
            autoComplete="off"
            required
            onChange={(event) => setSecret(event.target.value)}
          />
        </label>
        <p className="rs-explain">
          Stored encrypted and never shown again. SynAnalytics only reads; it cannot change anything
          in SynLedger.
        </p>
        {problem && (
          <p className="rh-access-msg rh-access-error" role="alert">
            {problem}
          </p>
        )}
        <div className="sp-actions">
          <button
            type="submit"
            className="btn"
            disabled={busy || !name.trim() || !clientId || !secret}
          >
            {busy ? 'Connecting…' : 'Connect and sync'}
          </button>
          <button type="button" className="btn secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
